// src/components/OrderItemsPicker.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Split from 'react-split';
import { FaTimes, FaSearch, FaPlus, FaMinus, FaTrash, FaCheck } from 'react-icons/fa';
import './OrderItemsPicker.css';

const API = import.meta.env.VITE_API_URL;

export default function OrderItemsPicker({
  open,
  onClose,
  onConfirm,
  order = {},
  clientid = '',
  warehouseid = '',
  /** Optional hooks from Orders.jsx; if absent we call the REST endpoints directly */
  fetchAvailable: fetchAvailableProp,
  persistPicks: persistPicksProp,
  allowBackorder = false,
  onBackorderAdd,
}) {
  if (!open) return null;

  const orderId = order?.orderid ?? order?.id ?? null;

  // Lock background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Set document title while picker is open
  useEffect(() => {
    const prevTitle = document.title;
    const ord = order?.ordernum ?? order?.orderid ?? orderId ?? '';
    const client = order?.clientname ? ' — ' + order.clientname : '';
    document.title = 'Request Inventory for Order #' + ord + client + ' | Easy3PL';
    return () => { document.title = prevTitle; };
  }, [orderId, order?.clientname]);

  // ---------- Filters ----------
  const [q, setQ] = useState('');
  const [receiptId, setReceiptId] = useState('');
  const [sku, setSku] = useState('');
  const [lot, setLot] = useState('');
  const [pallet, setPallet] = useState('');
  const [area, setArea] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  // Page size for available inventory fetches (backend defaults can be 50); bump to show more
  const FETCH_LIMIT = 500;
  const FETCH_OFFSET = 0;

  // ---------- Data ----------
  const [available, setAvailable] = useState([]);   // inventory rows from API
  const [selected, setSelected] = useState([]);     // items user chose for the order

  // Per-row input buffers: { [rowKey]: { u: string, p: string } }
  const [qtyInputs, setQtyInputs] = useState({});

  // ---- Row selection for Available table ----
  const [selectedInvIds, setSelectedInvIds] = useState([]);
  const isSelected = (id) => selectedInvIds.includes(Number(id));

  const toggleOne = (id) => {
    const nid = Number(id);
    const row = rowById.get(nid);
    if (!row) {
      // fallback: just toggle if row not found
      setSelectedInvIds(prev => prev.includes(nid) ? prev.filter(x => x !== nid) : [...prev, nid]);
      return;
    }
    const key = getKey(row);
    const uAvail = Number(row.__unitsAvail || 0);
    const pAvail = Number(row.__piecesAvail || 0);

    setSelectedInvIds(prev => {
      const already = prev.includes(nid);
      if (already) {
        // Uncheck → remove selection and clear inputs
        removeSelection(row);
        setQtyInputs(qp => ({ ...qp, [key]: { u: '', p: '' } }));
        return prev.filter(x => x !== nid);
      } else {
        // Check → qty-aware max-fill (pieces only stray beyond full units)
        const qpu = Number(row.qtyperunit || 0);
        const unitsToAdd = uAvail;
        const piecesToAdd = qpu > 0 ? Math.max(0, pAvail - unitsToAdd * qpu) : pAvail;
        setQtyInputs(qp => ({ ...qp, [key]: { u: String(unitsToAdd || 0), p: String(piecesToAdd || 0) } }));
        if (unitsToAdd > 0 || piecesToAdd > 0) {
          addSelection(row, unitsToAdd, piecesToAdd);
        }
        return [...prev, nid];
      }
    });
  };

  // ---------- Sorting (Available table) ----------
  // Default to receipt desc to mimic your backend default
  const [sortField, setSortField] = useState('receiptid');
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  const toggleSort = (field) => {
    setSortDir(prev => (field === sortField ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortField(field);
  };
  const ariaSort = (field) =>
    sortField !== field ? 'none' : (sortDir === 'asc' ? 'ascending' : 'descending');
  const indicator = (field) =>
    sortField === field ? <span className="sort-indicator" aria-hidden="true">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span> : null;

  // GET /api/orders/:id/available (fallback if no fetchAvailable prop passed)
  const fetchAvailableFallback = async (filters = {}) => {
    if (!orderId) return { data: [], meta: { limit: 0, offset: 0 } };
    const params = new URLSearchParams();
    const allow = ['q', 'receiptid', 'sku', 'lot', 'pallet', 'area', 'location', 'limit', 'offset'];
    allow.forEach(k => {
      const v = filters[k];
      if (v !== undefined && v !== null && String(v) !== '') params.set(k, v);
    });
    const url = `${API}/api/orders/${orderId}/available${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json(); // { data, meta }
  };

  // Fetch available inventory with filters (prefers prop if provided)
  const fetchAvailable = async () => {
    setLoading(true);
    try {
      const filters = { q, receiptid: receiptId, sku, lot, pallet, area, location, limit: FETCH_LIMIT, offset: FETCH_OFFSET };
      const out = fetchAvailableProp
        ? await fetchAvailableProp(filters)
        : await fetchAvailableFallback(filters);

      const rows = Array.isArray(out) ? out : (out?.data ?? []);
      setAvailable(rows);
    } catch {
      setAvailable([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load on open
  useEffect(() => {
    fetchAvailable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Helpers ----------
  const pad4 = (n) => (n || n === 0) ? String(n).padStart(4, '0') : '';
  // Human-friendly On Hand formatter using computed availability (post-deductions)
  const formatOnHand = (row) => {
    const p = Number(row?.__piecesAvail ?? 0);
    const u = Number(row?.__unitsAvail ?? 0);
    return u > 0 ? `${p} pcs (${u} units)` : `${p} pcs`;
  };
  // Location type label for available rows (1=FLOOR, 2=RACK, 6=PICKING)
  const fmtLocationType = (row) => {
    const t = String(row?.locationtype ?? '').trim();
    if (t === '6') return 'PICKING';
    if (t === '2') return 'RACK';
    if (t === '1') return 'FLOOR';
    const loc = String(row?.location || '').toUpperCase();
    if (loc.startsWith('FLOOR')) return 'FLOOR';
    return '';
  };
  const getKey = (row) =>
    row.__key ||
    row.inventoryid ||
    `${row.receiptid || ''}|${row.palletid || ''}|${row.sku || ''}|${row.lotnum || ''}|${row.serial || ''}`;

  /**
   * UPDATED AVAILABILITY MATH:
   * - Units selected reduce __unitsAvail
   * - ALSO reduce __piecesAvail by (unitsSelected * qtyperunit)
   * - Pieces selected reduce __piecesAvail by piecesSelected
   */
  const computedAvailable = useMemo(() => {
    if (!available.length) return available;

    const selUnits = new Map();
    const selPieces = new Map();

    for (const s of selected) {
      const key = s.__key || `${s.inventoryid || ''}|${s.receiptid || ''}|${s.palletid || ''}|${s.sku || ''}|${s.lotnum || ''}|${s.serial || ''}`;
      selUnits.set(key, (selUnits.get(key) || 0) + (Number(s.units) || 0));
      selPieces.set(key, (selPieces.get(key) || 0) + (Number(s.pieces) || 0));
    }

    return available.map(row => {
      const key = getKey(row);
      const usedUnits  = selUnits.get(key)  || 0;
      const usedPieces = selPieces.get(key) || 0;

      const unitsOnHand  = Number(row.boxes_on_hand ?? row.qtyunitsonhand ?? row.qtyunits ?? 0);
      const piecesOnHand = Number(row.pieces_on_hand ?? row.piecesonhand ?? row.pieces ?? 0);
      const qtyPerUnit   = Number(row.qtyperunit) || 0;
      const piecesConsumedByUnits = qtyPerUnit > 0 ? usedUnits * qtyPerUnit : 0;

      return {
        ...row,
        __key: key,
        __unitsAvail: Math.max(0, unitsOnHand  - usedUnits),
        __piecesAvail: Math.max(0, piecesOnHand - usedPieces - piecesConsumedByUnits),
      };
    });
  }, [available, selected]);

    // Sorted view of available
  const sortedAvailable = useMemo(() => {
    const arr = [...computedAvailable];

    const pick = (r, field) => {
      switch (field) {
        case 'receiptid':   return Number(r.receiptid || 0);
        case 'pallet':      return Number(r.pallet_number ?? r.palletnumber ?? 0);
        case 'areaname':    return String(r.warehouse_area ?? r.areaname ?? '');
        case 'location':    return String(r.location ?? '');
        case 'sku':         return String(r.sku ?? '');
        case 'itemdesc':    return String(r.item_description ?? r.itemdescription ?? r.description ?? '');
        case 'lotnum':      return String(r.lotnum ?? r.lot ?? '');
        case 'partnumber':  return String(r.partnumber ?? '');
        case 'onhand':      return Number(r.__piecesAvail ?? 0);
        case 'unitsavail':  return Number(r.__unitsAvail ?? r.boxes_on_hand ?? r.qtyunitsonhand ?? r.qtyunits ?? 0);
        case 'piecesavail': return Number(r.__piecesAvail ?? r.pieces_on_hand ?? r.piecesonhand ?? r.pieces ?? 0);
        default:            return '';
      }
    };

    const isNumberField = (field) =>
      ['receiptid','pallet','unitsavail','piecesavail','onhand'].includes(field);

    arr.sort((a, b) => {
      const av = pick(a, sortField);
      const bv = pick(b, sortField);

      let cmp = 0;
      if (isNumberField(sortField)) {
        cmp = (av ?? 0) - (bv ?? 0);
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
      }

      if (cmp === 0) {
        // stable-ish tie-breaker: SKU asc, then receipt desc (to mimic backend default)
        const t1 = String(a.sku ?? '').localeCompare(String(b.sku ?? ''), undefined, { sensitivity: 'base', numeric: true });
        if (t1 !== 0) cmp = t1;
        else cmp = Number(b.receiptid || 0) - Number(a.receiptid || 0);
      }

      return sortDir === 'asc' ? cmp : -cmp;
    });

    return arr;
  }, [computedAvailable, sortField, sortDir]);

    // IDs of currently visible available rows (for master checkbox)
  const availableIds = useMemo(() => (sortedAvailable || []).map(r => Number(r.inventoryid)).filter(Boolean), [sortedAvailable]);
  const allChecked = availableIds.length > 0 && selectedInvIds.length === availableIds.length;

  const toggleAll = () => {
    const allIds = availableIds;
    const selectingAll = !(selectedInvIds.length === allIds.length);

    if (selectingAll) {
      // Select all → for each row, qty-aware max-fill and add to selection
      const rows = allIds.map(id => rowById.get(id)).filter(Boolean);
      rows.forEach(row => {
        const key = getKey(row);
        const uAvail = Number(row.__unitsAvail || 0);
        const pAvail = Number(row.__piecesAvail || 0);
        const qpu = Number(row.qtyperunit || 0);
        const unitsToAdd = uAvail;
        const piecesToAdd = qpu > 0 ? Math.max(0, pAvail - unitsToAdd * qpu) : pAvail;
        setQtyInputs(qp => ({ ...qp, [key]: { u: String(unitsToAdd || 0), p: String(piecesToAdd || 0) } }));
        if (unitsToAdd > 0 || piecesToAdd > 0) addSelection(row, unitsToAdd, piecesToAdd);
      });
      setSelectedInvIds(allIds);
    } else {
      // Deselect all → clear inputs and remove from selection
      const rows = allIds.map(id => rowById.get(id)).filter(Boolean);
      rows.forEach(row => {
        const key = getKey(row);
        removeSelection(row);
        setQtyInputs(qp => ({ ...qp, [key]: { u: '', p: '' } }));
      });
      setSelectedInvIds([]);
    }
  };

  // Lookup computed row (with __unitsAvail/__piecesAvail) by inventory id
const rowById = useMemo(() => {
  const m = new Map();
  (sortedAvailable || []).forEach(r => {
    if (r?.inventoryid != null) m.set(Number(r.inventoryid), r);
  });
  return m;
}, [sortedAvailable]);

  // Add a row to the selection (clamped to availability)
  const addSelection = (row, unitsDelta = 1, piecesDelta = 0) => {
    if (!row) return;
    const units = Math.min(unitsDelta, row.__unitsAvail ?? 0);
    const pieces = Math.min(piecesDelta, row.__piecesAvail ?? 0);
    if (units <= 0 && pieces <= 0) return;

    const base = {
      inventoryid: row.inventoryid,
      receiptid: row.receiptid,
      palletid: row.palletid,
      palletnumber: row.pallet_number ?? row.palletnumber,
      palletstorageunit: row.storage_device ?? row.palletstorageunit ?? row.storageType,
      areaname: row.warehouse_area ?? row.areaname,
      location: row.location,
      sku: row.sku,
      itemdescription: row.item_description ?? row.itemdescription ?? row.description ?? '',
      lotnum: row.lotnum ?? row.lot,
      partnumber: row.partnumber,
      qtyperunit: Number(row.qtyperunit) || 0,
      units: 0,
      pieces: 0,
      __key: getKey(row),
    };

    setSelected(prev => {
      const idx = prev.findIndex(p => (p.__key || p.inventoryid) === base.__key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = {
          ...copy[idx],
          units:  (copy[idx].units  || 0) + units,
          pieces: (copy[idx].pieces || 0) + pieces,
        };
        return copy;
      }
      return [...prev, { ...base, units, pieces }];
    });
  };

  const decSelection = (row, unitsDelta = 1, piecesDelta = 0) => {
    setSelected(prev => {
      const idx = prev.findIndex(p => (p.__key || p.inventoryid) === row.__key);
      if (idx < 0) return prev;
      const cur = prev[idx];
      const nextUnits  = Math.max(0, (cur.units  || 0) - unitsDelta);
      const nextPieces = Math.max(0, (cur.pieces || 0) - piecesDelta);
      const next = [...prev];
      if (nextUnits === 0 && nextPieces === 0) {
        next.splice(idx, 1);
      } else {
        next[idx] = { ...cur, units: nextUnits, pieces: nextPieces };
      }
      return next;
    });
  };

  const removeSelection = (row) => {
    setSelected(prev => prev.filter(p => (p.__key || p.inventoryid) !== (row.__key || row.inventoryid)));
  };

  // Helpers for typed inputs
  const setUnitsInput = (key, val) =>
    setQtyInputs(prev => ({ ...prev, [key]: { ...(prev[key] || {}), u: val } }));
  const setPiecesInput = (key, val) =>
    setQtyInputs(prev => ({ ...prev, [key]: { ...(prev[key] || {}), p: val } }));

  const tryAddUnits = (row) => {
    const key = getKey(row);
    const raw = (qtyInputs[key]?.u ?? '').trim();
    const n = Math.max(0, parseInt(raw, 10) || 0);
    if (!n) return;
    addSelection(row, n, 0);
    setUnitsInput(key, '');
  };

  const tryAddPieces = (row) => {
    const key = getKey(row);
    const raw = (qtyInputs[key]?.p ?? '').trim();
    const n = Math.max(0, parseInt(raw, 10) || 0);
    if (!n) return;
    addSelection(row, 0, n);
    setPiecesInput(key, '');
  };

  // Live totals for selected items
  const selectedTotals = useMemo(() => {
    let units = 0, pieces = 0, totalPieces = 0;
    for (const s of selected) {
      const u = Number(s.units || 0);
      const p = Number(s.pieces || 0);
      const qpu = Number(s.qtyperunit || 0);
      units += u;
      pieces += p;
      totalPieces += u * qpu + p;
    }
    return { units, pieces, totalPieces };
  }, [selected]);

  // Clear Filters
  const hasFilters = q || receiptId || sku || lot || pallet || area || location;
  const clearFilters = () => {
    setQ('');
    setReceiptId('');
    setSku('');
    setLot('');
    setPallet('');
    setArea('');
    setLocation('');
    fetchAvailable();
  };

  // Finalize
  const handleConfirm = async () => {
    const items = selected
      .map(s => ({
        inventoryid: s.inventoryid ?? null,
        boxes: Number(s.units || 0),
        pieces: Number(s.pieces || 0),
      }))
      .filter(r => r.inventoryid && (r.boxes > 0 || r.pieces > 0));

    if (persistPicksProp) {
      await onConfirm?.(items);
      return;
    }

    try {
      if (!orderId) throw new Error('Missing order id');
      const res = await fetch(`${API}/api/orders/${orderId}/items/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.error || `${res.status} ${res.statusText}`);
      }
      onConfirm?.(items);
    } catch (err) {
      alert(`Failed to add items: ${err.message}`);
    }
  };

  const keyToSearch = (e) => { if (e.key === 'Enter') fetchAvailable(); };

  // ---- Out-of-Stock (Back Order) modal ----
  const [oosOpen, setOosOpen] = useState(false);
  const [oosLoading, setOosLoading] = useState(false);
  const [oosSearch, setOosSearch] = useState('');
  const [oosItems, setOosItems] = useState([]);
  const [oosSelectedSku, setOosSelectedSku] = useState('');
  const [oosQty, setOosQty] = useState(1);

  const openOosModal = async () => {
    try {
      if (!allowBackorder || !orderId || !clientid) return;
      setOosOpen(true);
      setOosLoading(true);
      const params = new URLSearchParams({ clientid: String(clientid), limit: '500' });
      if (oosSearch) params.set('q', oosSearch);
      const res = await fetch(`${API}/api/items?${params.toString()}`);
      const j = await res.json().catch(()=>({ data: [] }));
      const rows = Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : [];
      const zeros = rows.filter(r => (Number(r.qtyunitsonhand || 0) + Number(r.piecesonhand || 0)) <= 0);
      setOosItems(zeros);
      setOosSelectedSku(zeros[0]?.sku || '');
      setOosQty(1);
    } catch { setOosItems([]); } finally { setOosLoading(false); }
  };

  const addOosToOrder = async () => {
    try {
      const sku = String(oosSelectedSku || '').trim();
      const qty = Math.max(1, Number(oosQty || 0));
      if (!orderId || !sku || !qty) return;
      // Only supported for eOrders: POST /api/eorders/:id/items
      const body = { items: [{ sku, qty }] };
      const res = await fetch(`${API}/api/eorders/${orderId}/items`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const j = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(j?.error || 'Failed to add item');
      alert(`Added ${qty} × ${sku} (back order)`);
      setOosOpen(false);
      try { if (typeof onBackorderAdd === 'function') onBackorderAdd(orderId, { sku, qty }); } catch {}
    } catch (e) {
      alert(e?.message || 'Failed to add out-of-stock item');
    }
  };

  // ---------- UI ----------
  const content = (
    <div className="order-items-picker-overlay">
      <div className="order-items-picker-modal" role="dialog" aria-modal="true" aria-label="Request Inventory for Order">
        {/* Header */}
        <div className="order-items-picker-header">
          <div className="order-items-picker-title">
            Request Inventory for eOrder{' '}
            <span className="order-items-picker-accent">
              #{order?.ordernum ?? order?.orderid ?? ''}
            </span>
          </div>
          <button className="order-items-picker-iconbtn" onClick={onClose} title="Close">
            <FaTimes />
          </button>
        </div>

        {/* Order info line */}
        <div className="order-items-picker-orderline">
          <span><strong>Client:</strong> {order?.clientname || '-'}</span>
          <span><strong>Created:</strong> {order?.created ? new Date(order.created).toLocaleString() : '-'}</span>
        </div>

        {/* Filters */}
        <div className="order-items-picker-filters">
          <div className="order-items-picker-input-with-icon">
            <FaSearch className="order-items-picker-input-icon" />
            <input
              className="order-items-picker-input"
              placeholder="Search description, part #, lot, etc."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={keyToSearch}
            />
          </div>
          <input
            className="order-items-picker-input"
            placeholder="Receipt # (e.g., 123)"
            value={receiptId}
            onChange={(e) => setReceiptId(e.target.value)}
            onKeyDown={keyToSearch}
          />
          <input
            className="order-items-picker-input"
            placeholder="SKU"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            onKeyDown={keyToSearch}
          />
          <input
            className="order-items-picker-input"
            placeholder="LOT"
            value={lot}
            onChange={(e) => setLot(e.target.value)}
            onKeyDown={keyToSearch}
          />
          <input
            className="order-items-picker-input"
            placeholder="Pallet #"
            inputMode="numeric"
            value={pallet}
            onChange={(e) => setPallet(e.target.value.replace(/\D+/g, ''))}
            onKeyDown={keyToSearch}
          />
          <input
            className="order-items-picker-input"
            placeholder="Area"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            onKeyDown={keyToSearch}
          />
          <input
            className="order-items-picker-input"
            placeholder="Location (row/col/shelf/bin)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onKeyDown={keyToSearch}
          />

          <button className="order-items-picker-btn" onClick={fetchAvailable} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
          <button
            className="order-items-picker-btn"
            onClick={clearFilters}
            disabled={!hasFilters}
            title="Clear all filters"
          >
            Clear
          </button>
          {allowBackorder && (
            <button
              className="order-items-picker-btn"
              onClick={openOosModal}
              title="Show Items Not In Stock"
            >
              Show Items Not In Stock
            </button>
          )}
        </div>

        {/* Split view */}
        <div className="order-items-picker-splitwrap">
          <Split
            direction="vertical"
            sizes={[60, 40]}
            minSize={[180, 140]}
            gutterSize={12}
            className="order-items-picker-split"
          >
            {/* Top pane: available */}
            <div className="order-items-picker-pane">
              <div className="order-items-picker-pane-title">Available Inventory</div>
              <div className="order-items-picker-tablewrap">
                <table className="order-items-picker-table">
                  <thead>
                    <tr>
                      <th className="col-select">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={toggleAll}
                          aria-label="Select all"
                        />
                      </th>
                      <th>Add Unit(s)</th>
                      <th>Add Piece(s)</th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('receiptid')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('receiptid'); }}
                        className={`sortable ${sortField === 'receiptid' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('receiptid')}
                      >
                        Receipt{indicator('receiptid')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('pallet')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('pallet'); }}
                        className={`sortable ${sortField === 'pallet' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('pallet')}
                      >
                        Pallet{indicator('pallet')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('areaname')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('areaname'); }}
                        className={`sortable ${sortField === 'areaname' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('areaname')}
                      >
                        Area{indicator('areaname')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('location')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('location'); }}
                        className={`sortable ${sortField === 'location' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('location')}
                      >
                        Location{indicator('location')}
                      </th>
                      <th>Location Type</th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('sku')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('sku'); }}
                        className={`sortable ${sortField === 'sku' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('sku')}
                      >
                        SKU{indicator('sku')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('itemdesc')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('itemdesc'); }}
                        className={`sortable ${sortField === 'itemdesc' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('itemdesc')}
                      >
                        Description{indicator('itemdesc')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('lotnum')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('lotnum'); }}
                        className={`sortable ${sortField === 'lotnum' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('lotnum')}
                      >
                        LOT{indicator('lotnum')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('partnumber')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('partnumber'); }}
                        className={`sortable ${sortField === 'partnumber' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('partnumber')}
                      >
                        Part #{indicator('partnumber')}
                      </th>
                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('onhand')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('onhand'); }}
                        className={`sortable ${sortField === 'onhand' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('onhand')}
                      >
                        On Hand{indicator('onhand')}
                      </th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('unitsavail')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('unitsavail'); }}
                        className={`sortable text-right ${sortField === 'unitsavail' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('unitsavail')}
                      >
                        Units Avail{indicator('unitsavail')}
                      </th>

                      {/* New column: Pieces Per Unit */}
                      <th className="text-right">Pieces Per Unit</th>

                      <th
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSort('piecesavail')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSort('piecesavail'); }}
                        className={`sortable text-right ${sortField === 'piecesavail' ? (sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc') : ''}`}
                        aria-sort={ariaSort('piecesavail')}
                      >
                        Pieces Avail{indicator('piecesavail')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAvailable.length ? (() => {
                      // Helper to add all available for a row
                      const addAllForRow = (row) => {
                        if (!row) return;
                        // Use current computed availability (post-deductions) and add whatever remains
                        const uRemain = Number(row.__unitsAvail || 0);
                        const pRemain = Number(row.__piecesAvail || 0);
                        const qpu = Number(row.qtyperunit || 0);
                        if (uRemain <= 0 && pRemain <= 0) return;
                        const unitsToAdd = uRemain;
                        const piecesToAdd = qpu > 0 ? Math.max(0, pRemain - unitsToAdd * qpu) : pRemain;
                        const key = getKey(row);
                        setQtyInputs(qp => ({ ...qp, [key]: { u: String(unitsToAdd || ''), p: String(piecesToAdd || '') } }));
                        addSelection(row, unitsToAdd, piecesToAdd);
                      };
                      return sortedAvailable.map(row => {
                        const key = getKey(row);
                        const uStr = qtyInputs[key]?.u ?? '';
                        const pStr = qtyInputs[key]?.p ?? '';
                        const uNum = Number.isFinite(parseInt(uStr, 10)) ? parseInt(uStr, 10) : 0;
                        const pNum = Number.isFinite(parseInt(pStr, 10)) ? parseInt(pStr, 10) : 0;

                        const uAvail = row.__unitsAvail ?? 0;
                        const pAvail = row.__piecesAvail ?? 0;

                        const canAddUnits  = uNum > 0 && uAvail > 0 && uNum <= uAvail;
                        const canAddPieces = pNum > 0 && pAvail > 0 && pNum <= pAvail;

                        return (
                          <tr key={row.inventoryid ?? row.__key}>
                            <td className="col-select">
                              <input
                                type="checkbox"
                                checked={isSelected(row.inventoryid)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleOne(row.inventoryid)}
                                aria-label={`Select inventory ${row.inventoryid}`}
                              />
                              {isSelected(row.inventoryid) && (Number(row.__unitsAvail || 0) > 0 || Number(row.__piecesAvail || 0) > 0) && (
                                <button
                                  className="order-items-picker-mini-btn"
                                  style={{ marginLeft: 4 }}
                                  title="Add all remaining units and pieces"
                                  onClick={(e) => { e.stopPropagation(); addAllForRow(row); }}
                                >
                                  All
                                </button>
                              )}
                            </td>
                            <td className="nowrap">
                              {/* Units input + add */}
                              <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={uAvail}
                                step={1}
                                value={uStr}
                                onChange={(e) => setUnitsInput(key, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && canAddUnits) tryAddUnits(row); }}
                                aria-label="Units to add"
                                className="order-items-picker-qty-input"
                                placeholder="0"
                              />
                              <button
                                className="order-items-picker-mini-btn accent"
                                title="Add units"
                                onClick={() => tryAddUnits(row)}
                                disabled={isSelected(row.inventoryid) || !canAddUnits}
                              >
                                <FaPlus />
                              </button>
                            </td>
                            <td className="nowrap">
                              {/* Pieces input + add */}
                              <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={pAvail}
                                step={1}
                                value={pStr}
                                onChange={(e) => setPiecesInput(key, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && canAddPieces) tryAddPieces(row); }}
                                aria-label="Pieces to add"
                                className="order-items-picker-qty-input"
                                placeholder="0"
                              />
                              <button
                                className="order-items-picker-mini-btn accent"
                                title="Add pieces"
                                onClick={() => tryAddPieces(row)}
                                disabled={isSelected(row.inventoryid) || !canAddPieces}
                              >
                                <FaPlus />
                              </button>
                            </td>

                            <td>{row.receiptid ? `WR${row.receiptid}` : ''}</td>
                            <td>{pad4(row.pallet_number ?? row.palletnumber)}</td>
                            <td>{row.warehouse_area ?? row.areaname}</td>
                            <td className="nowrap">{row.location}</td>
                            <td className="nowrap">{fmtLocationType(row)}</td>
                            <td>{row.sku}</td>
                            <td>{row.item_description ?? row.itemdescription ?? row.description}</td>
                            <td>{row.lotnum ?? row.lot}</td>
                            <td>{row.partnumber}</td>
                            <td className="text-right">{formatOnHand(row)}</td>
                            <td className="text-right">{uAvail}</td>
                            <td className="text-right">{Number(row.qtyperunit || 0)}</td>
                            <td className="text-right">{pAvail}</td>
                          </tr>
                        );
                      });
                    })() : (
                      <tr><td colSpan={16} className="muted">No inventory found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bottom pane: selected */}
            <div className="order-items-picker-pane">
              <div className="order-items-selected-pane-title" >
                <span>Selected for Order - </span>
                
                  Units/Boxes selected: {selectedTotals.units.toLocaleString()} &nbsp;|
                  &nbsp; Single Pieces Selected: {selectedTotals.pieces.toLocaleString()} &nbsp;|
                  &nbsp; Total Pieces Selected: {selectedTotals.totalPieces.toLocaleString()}
                
              </div>
              <div className="order-items-picker-tablewrap">
                <table className="order-items-picker-table">
                  <thead>
                    <tr>
                      <th className="col-adjust">Adjust</th>
                      <th>Receipt</th>
                      <th>Pallet</th>
                      <th>Area</th>
                      <th>Location</th>
                      <th>SKU</th>
                      <th>Description</th>
                      <th>LOT</th>
                      <th>Part #</th>
                      <th className="text-right">Units</th>
                      <th className="text-right">Pieces</th>
                      <th className="col-remove">Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.length ? selected.map(row => (
                      <tr key={row.__key || row.inventoryid}>
                        <td className="nowrap">
                          <button className="order-items-picker-mini-btn accent" title="–1 unit" onClick={() => decSelection(row, 1, 0)}>
                            <FaMinus />
                          </button>
                        </td>
                        <td>{row.receiptid ? `WR${row.receiptid}` : ''}</td>
                        <td>{pad4(row.palletnumber)}</td>
                        <td>{row.areaname}</td>
                        <td className="nowrap">{row.location}</td>
                        <td>{row.sku}</td>
                        <td>{row.itemdescription}</td>
                        <td>{row.lotnum}</td>
                        <td>{row.partnumber}</td>
                        <td className="text-right">{row.units || 0}</td>
                        <td className="text-right">{row.pieces || 0}</td>
                        <td>
                          <button className="order-items-picker-mini-btn danger" title="Remove row" onClick={() => removeSelection(row)}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={12} className="muted">Nothing selected</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Split>
        </div>

        {/* Footer actions */}
        <div className="order-items-picker-footer">
          <button className="order-items-picker-btn" onClick={onClose}>
            <FaTimes style={{ marginRight: 6 }} /> Cancel
          </button>
          <button
            className="order-items-picker-btn order-items-picker-btn-primary"
            onClick={handleConfirm}
            disabled={!selected.length}
            title={selected.length ? '' : 'Nothing selected yet'}
          >
            <FaCheck style={{ marginRight: 6 }} /> Save
          </button>
        </div>
      </div>
      {allowBackorder && oosOpen && (
        <div className="order-items-picker-overlay" onClick={() => setOosOpen(false)}>
          <div className="order-items-picker-modal" role="dialog" aria-modal="true" aria-labelledby="oosTitle" onClick={e => e.stopPropagation()}>
            <div className="order-items-picker-header">
              <div className="order-items-picker-title">
                Items Not In Stock — Client {order?.clientname || ''}
              </div>
              <button className="order-items-picker-iconbtn" onClick={() => setOosOpen(false)} title="Close">
                <FaTimes />
              </button>
            </div>
            <div className="order-items-picker-filters">
              <div className="order-items-picker-input-with-icon">
                <FaSearch className="order-items-picker-input-icon" />
                <input
                  className="order-items-picker-input"
                  placeholder="Search SKU or description"
                  value={oosSearch}
                  onChange={(e) => setOosSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openOosModal(); }}
                />
              </div>
              <button className="order-items-picker-btn" onClick={openOosModal} disabled={oosLoading}>
                {oosLoading ? 'Searching…' : 'Search'}
              </button>
            </div>
            <div className="order-items-picker-splitwrap">
              <div className="order-items-picker-pane" style={{ minHeight: 160 }}>
                <div className="order-items-picker-pane-title">Zero-Stock Items</div>
                <div className="order-items-picker-tablewrap">
                  <table className="order-items-picker-table">
                    <thead>
                      <tr><th></th><th>SKU</th><th>Description</th><th>On Hand</th></tr>
                    </thead>
                    <tbody>
                      {oosItems.length ? oosItems.map((it, idx) => (
                        <tr key={`${it.itemid || it.sku || idx}`} onClick={() => setOosSelectedSku(it.sku)} style={{ cursor: 'pointer' }}>
                          <td><input type="radio" name="oosPick" checked={oosSelectedSku === it.sku} onChange={() => setOosSelectedSku(it.sku)} /></td>
                          <td>{it.sku}</td>
                          <td>{it.description || ''}</td>
                          <td>0</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={4} className="muted">{oosLoading ? 'Loading…' : 'No zero-stock items found'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label>Qty
                    <input type="number" min={1} value={oosQty} onChange={(e) => setOosQty(Math.max(1, Number(e.target.value || 1)))} style={{ width: 80, marginLeft: 6 }} />
                  </label>
                </div>
              </div>
            </div>
            <div className="order-items-picker-footer">
              <button className="order-items-picker-btn" onClick={() => setOosOpen(false)}>
                <FaTimes style={{ marginRight: 6 }} /> Cancel
              </button>
              <button className="order-items-picker-btn order-items-picker-btn-primary" onClick={addOosToOrder} disabled={!oosSelectedSku}>
                <FaCheck style={{ marginRight: 6 }} /> Add To Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
}
